/******************************************************************************
 * useUserRoles: read the current user's realm roles.                         *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * MANAGER_ROLES: roles allowed into the statistics module
  * useUserRoles: hook exposing role checks
*/

/* Imports */
import { useKeycloak } from "@react-keycloak/web";
import { UserRole } from "./useWorkorderPermissions";
/***/

/**
 * Roles allowed into the statistics module — mirrors the backend's `@Planner` guard on
 * `/stats/**`: manager and above, never a technician (technicians get their own figures in the
 * mobile app).
 */
export const MANAGER_ROLES: string[] = [
  UserRole.PLANNER,
  UserRole.TEAM_LEADER,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN
];

/**
 * useUserRoles: role checks against the Keycloak token.
 *
 * Used to hide UI a user cannot act on. The API stays the authority — hiding a block is a courtesy,
 * not a protection, and the corresponding endpoint answers 403 regardless.
 */
export function useUserRoles() {
  const { keycloak } = useKeycloak();
  const roles = keycloak.realmAccess?.roles ?? [];

  const hasAnyRole = (allowed: string[]): boolean =>
    allowed.some((role) => roles.includes(role));

  return {
    roles,
    hasAnyRole,
    /** Manager or above: may open the statistics module. */
    isManager: hasAnyRole(MANAGER_ROLES),
    /** Admin or above: may see revenue and other restricted blocks. */
    isAdmin: hasAnyRole([UserRole.ADMIN, UserRole.SUPER_ADMIN]),
    /** Super admin: may edit what normally belongs to an integration, such as an Odoo id. */
    isSuperAdmin: hasAnyRole([UserRole.SUPER_ADMIN])
  };
}
/***/
